import { memo } from "react";
import { Card, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { HelpdeskTicketInterface, TicketPriority, TicketLabel } from "shared/types/helpdesk";
import {
  Building,
  User,
  Clock,
  Wrench,
  Star,
  Envelope,
  Paperclip,
  Phone,
  WhatsappLogo,
  Desktop,
  Tag,
  Cube,
  CalendarBlank,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface HelpdeskCardProps {
  ticket: HelpdeskTicketInterface;
  onClick: () => void;
  fontSize?: number;
}

const HelpdeskCardComponent = ({ ticket, onClick, fontSize = 12 }: HelpdeskCardProps) => {
  // Calculate derived font sizes
  const titleFontSize = fontSize;
  const metadataFontSize = Math.max(fontSize - 2, 10);
  const iconSize = Math.max(fontSize - 2, 10);
  const hasValidCreatedAt = ticket.createdAt && !isNaN(new Date(ticket.createdAt).getTime());
  const reporter = ticket.reporter as any;
  const reporterName = reporter ? `${reporter.firstName || ''} ${reporter.lastName || ''}`.trim() : 'Onbekend';

  // Priority stars
  const getPriorityStars = (priority: TicketPriority) => {
    const starCount = priority === TicketPriority.HIGH ? 3 : priority === TicketPriority.MEDIUM ? 2 : priority === TicketPriority.LOW ? 1 : 0;
    return starCount;
  };

  const priorityStars = getPriorityStars(ticket.priority);

  // Source icon - Note: TicketSource enum doesn't have these values
  // This appears to be showing communication method, not source
  const getSourceIcon = () => {
    const sourceStr = String(ticket.source || '');
    if (sourceStr.includes('Phone')) {
      return <Phone weight="duotone" size={10} />;
    } else if (sourceStr.includes('WhatsApp')) {
      return <WhatsappLogo weight="duotone" size={10} />;
    } else if (sourceStr.includes('Email')) {
      return <Envelope weight="duotone" size={10} />;
    } else if (sourceStr.includes('Web')) {
      return <Desktop weight="duotone" size={10} />;
    } else {
      return <Desktop weight="duotone" size={10} />;
    }
  };

  // Get short source label (first letter or abbreviation)
  const getSourceLabel = () => {
    const sourceStr = String(ticket.source || '');
    if (sourceStr.includes('Phone')) return 'T';
    if (sourceStr.includes('WhatsApp')) return 'W';
    if (sourceStr.includes('Email')) return 'E';
    if (sourceStr.includes('Web')) return 'I';
    return sourceStr.charAt(0).toUpperCase();
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200 border-neutral-200 hover:border-moss-primary/30 bg-white overflow-hidden will-change-auto"
      onClick={onClick}
    >
      <CardContent className="p-2 space-y-1.5 overflow-hidden">
        {/* Header: Ticket ID + Priority + Source */}
        <div className="flex items-center justify-between gap-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {/* Ticket ID - prominent */}
            <span
              className="font-bold text-moss-primary shrink-0"
              style={{ fontSize: `${titleFontSize}px` }}
            >
              H{ticket.id}
            </span>

            {/* Priority Stars - compact */}
            {priorityStars > 0 && (
              <div className="flex items-center gap-0.5 shrink-0">
                {[...Array(priorityStars)].map((_, i) => (
                  <Star key={i} weight="fill" size={iconSize} className="text-yellow-500" />
                ))}
              </div>
            )}
          </div>

          {/* Source indicator - minimal */}
          <div
            className="flex items-center gap-0.5 text-neutral-500 shrink-0"
            style={{ fontSize: `${metadataFontSize}px` }}
            title={String(ticket.source)}
          >
            {getSourceIcon()}
            <span className="hidden sm:inline">{getSourceLabel()}</span>
          </div>
        </div>

        {/* Title - 2 lines max, compact */}
        <h4
          className="font-medium text-neutral-900 line-clamp-2 leading-snug"
          style={{ fontSize: `${titleFontSize}px` }}
        >
          {ticket.title}
        </h4>

        {/* Building - separate line */}
        {ticket.building && (
          <div className="flex items-center gap-0.5 min-w-0" style={{ fontSize: `${metadataFontSize}px` }}>
            <Building weight="duotone" size={iconSize} className="text-moss-primary shrink-0" />
            <span className="font-medium text-moss-primary truncate">
              {ticket.building.id}
            </span>
          </div>
        )}

        {/* Unit - separate line */}
        {ticket.unit && (
          <div className="flex items-center gap-0.5 min-w-0" style={{ fontSize: `${metadataFontSize}px` }}>
            <Cube weight="duotone" size={iconSize} className="text-blue-600 shrink-0" />
            <span className="font-medium text-blue-600 truncate">
              {ticket.unit.boxNumber || ticket.unit.id}
            </span>
          </div>
        )}

        {/* Label - minimal badge */}
        {ticket.label && ticket.label !== TicketLabel.UNKNOWN && (
          <div className="flex items-center gap-1 min-w-0">
            <Tag weight="duotone" size={iconSize} className="text-neutral-400 shrink-0" />
            <span className="text-neutral-600 truncate" style={{ fontSize: `${metadataFontSize}px` }}>
              {ticket.label}
            </span>
          </div>
        )}

        {/* Reporter - separate line */}
        <div className="flex items-center gap-0.5 text-neutral-600 min-w-0" style={{ fontSize: `${metadataFontSize}px` }}>
          <User weight="duotone" size={iconSize} className="shrink-0 text-neutral-400" />
          <span className="truncate">{reporterName}</span>
        </div>

        {/* Supplier - separate line (only if exists) */}
        {ticket.supplier && (
          <div className="flex items-center gap-0.5 text-neutral-600 min-w-0" style={{ fontSize: `${metadataFontSize}px` }}>
            <Wrench weight="duotone" size={iconSize} className="shrink-0 text-neutral-400" />
            <span className="truncate">{ticket.supplier.name}</span>
          </div>
        )}

        {/* Days in Phase - if significant */}
        {ticket.daysInCurrentPhase !== undefined && ticket.daysInCurrentPhase > 3 && (
          <div className="flex items-center gap-1">
            <Clock
              weight="duotone"
              size={iconSize}
              className={cn(
                "shrink-0",
                ticket.daysInCurrentPhase > 7 ? "text-red-500" :
                ticket.daysInCurrentPhase > 3 ? "text-orange-500" :
                "text-neutral-400"
              )}
            />
            <span
              className={cn(
                "font-medium",
                ticket.daysInCurrentPhase > 7 ? "text-red-700" :
                ticket.daysInCurrentPhase > 3 ? "text-orange-700" :
                "text-neutral-600"
              )}
              style={{ fontSize: `${metadataFontSize}px` }}
            >
              {ticket.daysInCurrentPhase}d
            </span>
          </div>
        )}

        {/* Interventie gepland — count of planned interventions, orange when overdue */}
        {((ticket as any).interventionCount || 0) > 0 && (
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold",
              (ticket as any).hasOverdueIntervention
                ? "bg-orange-100 text-orange-700 ring-1 ring-orange-300"
                : "bg-blue-100 text-blue-700",
            )}
            title={`${(ticket as any).interventionCount} geplande interventie(s)${
              (ticket as any).hasOverdueIntervention
                ? " — minstens één is voorbij"
                : ""
            }`}
            style={{ fontSize: `${metadataFontSize}px` }}
          >
            <CalendarBlank weight="duotone" size={iconSize} className="shrink-0" />
            <span>{(ticket as any).interventionCount}</span>
          </div>
        )}

        {/* Bottom indicators: Email & Attachments */}
        <div className="flex items-center gap-2 pt-0.5 border-t border-neutral-100" style={{ fontSize: `${metadataFontSize}px` }}>
          {/* Email indicator */}
          {ticket.emailCount !== undefined && ticket.emailCount > 0 && (
            <div
              className={cn(
                "flex items-center gap-0.5",
                ticket.hasUnreadEmails ? "text-moss-primary font-semibold" : "text-neutral-500"
              )}
            >
              <Envelope
                weight={ticket.hasUnreadEmails ? 'fill' : 'duotone'}
                size={iconSize}
                className="shrink-0"
              />
              <span>{ticket.emailCount}</span>
              {ticket.unreadEmailCount !== undefined && ticket.unreadEmailCount > 0 && (
                <Badge className="h-3.5 px-1 leading-none bg-moss-primary text-white ml-0.5" style={{ fontSize: `${Math.max(metadataFontSize - 1, 9)}px` }}>
                  {ticket.unreadEmailCount}
                </Badge>
              )}
            </div>
          )}

          {/* Attachments indicator */}
          {ticket.hasAttachments && (
            <div className="flex items-center gap-0.5 text-neutral-500">
              <Paperclip weight="duotone" size={iconSize} />
            </div>
          )}

          {/* Created date - compact, right-aligned */}
          {hasValidCreatedAt && ticket.createdAt && (
            <div className="ml-auto text-neutral-400 shrink-0">
              {format(new Date(ticket.createdAt), 'dd/MM')}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render if ticket.id or ticket.internalStatus changes
export const HelpdeskCard = memo(HelpdeskCardComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these key properties change
  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.internalStatus === nextProps.ticket.internalStatus &&
    prevProps.ticket.priority === nextProps.ticket.priority &&
    prevProps.ticket.emailCount === nextProps.ticket.emailCount &&
    prevProps.ticket.unreadEmailCount === nextProps.ticket.unreadEmailCount &&
    prevProps.ticket.title === nextProps.ticket.title &&
    prevProps.ticket.daysInCurrentPhase === nextProps.ticket.daysInCurrentPhase &&
    prevProps.fontSize === nextProps.fontSize
  );
});

